import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Paper, Button, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, IconButton, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Tooltip, CircularProgress, Snackbar, Alert, Chip,
} from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon, Refresh as RefreshIcon, Place as PlaceIcon, Close as CloseIcon } from '@mui/icons-material';
import axios from 'axios';
import { getApiUrl } from '../../config/environment';
import { usePageBreakpoints, pageContainerSx } from '../../hooks/useClinicPageBreakpoints';


const InventoryLocations: React.FC = () => {
  const { isMobile } = usePageBreakpoints();

  const [locations, setLocations] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState({ name: '', unit_name: '', description: '' });
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });

  const fetch = useCallback(async () => {
    setLoading(true);
    try { const res = await axios.get(getApiUrl('/inventory-module/locations')); setLocations(res.data.data || []); }
    catch (e) { console.error(e); } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetch(); }, [fetch]);

  const openDialog = (loc?: any) => {
    if (loc) { setEditingId(loc.id); setForm({ name: loc.name || '', unit_name: loc.unit_name || '', description: loc.description || '' }); }
    else { setEditingId(null); setForm({ name: '', unit_name: '', description: '' }); }
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.name.trim()) { setSnackbar({ open: true, message: 'Nome obrigatório', severity: 'error' }); return; }
    try {
      setSaving(true);
      if (editingId) await axios.put(getApiUrl(`/inventory-module/locations/${editingId}`), form);
      else await axios.post(getApiUrl('/inventory-module/locations'), form);
      setSnackbar({ open: true, message: editingId ? 'Atualizada!' : 'Criada!', severity: 'success' });
      setDialogOpen(false); fetch();
    } catch (e: any) { setSnackbar({ open: true, message: e.response?.data?.error || 'Erro', severity: 'error' }); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Desativar esta localização?')) return;
    try { await axios.delete(getApiUrl(`/inventory-module/locations/${id}`)); setSnackbar({ open: true, message: 'Desativada!', severity: 'success' }); fetch(); }
    catch (e) { setSnackbar({ open: true, message: 'Erro', severity: 'error' }); }
  };

  return (
    <Box sx={pageContainerSx}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <PlaceIcon sx={{ fontSize: 32, color: '#8b5cf6' }} />
          <Typography variant="h5" sx={{ fontWeight: 700 }}>Locais de Produtos</Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Tooltip title="Atualizar"><IconButton onClick={fetch}><RefreshIcon /></IconButton></Tooltip>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => openDialog()}
            sx={{ bgcolor: '#8b5cf6', '&:hover': { bgcolor: '#7c3aed' }, textTransform: 'none', fontWeight: 600 }}>
            Nova Localização
          </Button>
        </Box>
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress sx={{ color: '#8b5cf6' }} /></Box>
      ) : (
        <TableContainer component={Paper} sx={{ borderRadius: 2 }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: '#f9fafb' }}>
                <TableCell sx={{ fontWeight: 700 }}>Localização</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Unidade</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Descrição</TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="center">Status</TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="center">Ações</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {locations.length === 0 ? (
                <TableRow><TableCell colSpan={5} align="center" sx={{ py: 4, color: '#9ca3af' }}>Nenhuma localização cadastrada</TableCell></TableRow>
              ) : locations.map(loc => (
                <TableRow key={loc.id} hover>
                  <TableCell><Typography variant="body2" sx={{ fontWeight: 600 }}>{loc.name}</Typography></TableCell>
                  <TableCell><Typography variant="body2">{loc.unit_name || '-'}</Typography></TableCell>
                  <TableCell><Typography variant="body2" color="text.secondary">{loc.description || '-'}</Typography></TableCell>
                  <TableCell align="center">
                    <Chip label={loc.is_active ? 'Ativa' : 'Inativa'} size="small" sx={{ bgcolor: loc.is_active ? '#d1fae5' : '#f3f4f6', color: loc.is_active ? '#059669' : '#6b7280', fontWeight: 600 }} />
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip title="Editar"><IconButton size="small" onClick={() => openDialog(loc)}><EditIcon sx={{ fontSize: 16 }} /></IconButton></Tooltip>
                    <Tooltip title="Desativar"><IconButton size="small" onClick={() => handleDelete(loc.id)}><DeleteIcon sx={{ fontSize: 16, color: '#ef4444' }} /></IconButton></Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog fullScreen={isMobile} scroll="paper" open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, display: 'flex', justifyContent: 'space-between' }}>
          {editingId ? 'Editar Localização' : 'Nova Localização'}
          <IconButton onClick={() => setDialogOpen(false)}><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField fullWidth required label="Nome da Localização *" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} size="small" placeholder="Ex: ESTOQUE PADRÃO PRATELEIRA A" />
            <TextField fullWidth label="Unidade / Filial" value={form.unit_name} onChange={(e) => setForm({ ...form, unit_name: e.target.value })} size="small" placeholder="Ex: Matriz, Filial Centro" />
            <TextField fullWidth label="Descrição" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} size="small" multiline rows={2} />
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setDialogOpen(false)} sx={{ textTransform: 'none' }}>Cancelar</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving}
            sx={{ bgcolor: '#8b5cf6', '&:hover': { bgcolor: '#7c3aed' }, textTransform: 'none', fontWeight: 600 }}>
            {saving ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : 'Salvar'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar({ ...snackbar, open: false })} anchorOrigin={{ vertical: 'bottom', horizontal: isMobile ? 'center' : 'right' }}>
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
};

export default InventoryLocations;
